<script>
import appConfig from '@src/app.config.json'
import Layout from '@layouts/user-management.vue'
import { httpServices } from '@utils/http-services'
import { hasErrors } from '@utils/form'
import { timezones } from '@utils/timezones'
import HardwareSummaryTable from '@components/tables/hardware-summary-table.vue'

const getSim = (i, sims) => {
  if (i?.remote_sim_info?.imsi) {
    return sims.filter((s) => s.meta.imsi === i.remote_sim_info.imsi)[0]
  }
  return null
}

export default {
  page: {
    title: 'Hardware Summary',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: { Layout, HardwareSummaryTable },
  data() {
    return {
      hasErrors,
      hardware: null,
      show: false,
      summary: null,
      sims: null,
      timezones: timezones,
      formLayout: 'horizontal',
    }
  },
  mounted() {
    httpServices.getHardware(this.$route.params.hardwareId).then((res) => {
      this.hardware = res
      httpServices.getSims().then((sims) => {
        this.sims = sims
        httpServices
          .getConnectionsByHardware(this.$route.params.hardwareId)
          .then((connections) => {
            this.connections = connections
            httpServices.getHardwareStatus().then((status) => {
              const summary = status.data.data.filter(
                (s) => s.sn === this.hardware.meta.serial
              )[0]
              if (summary) {
                this.hardware.meta.status = summary.status
              } else {
                this.hardware.meta.status = 'offline'
              }

              if (summary?.interfaces?.length) {
                summary.interfaces = summary?.interfaces
                  .map((i, n) => {
                    return {
                      ...i,
                      key: n,
                      carrier_name: i?.carrier_name ? i.carrier_name : '',
                      signal_bar: i?.signal_bar > 0 ? i.signal_bar : '',
                      imsi: i?.remote_sim_info ? i.remote_sim_info.imsi : '',
                      sim: getSim(i, this.sims),
                      connection: this.connections.filter(
                        (c) => c.meta.id === i.id
                      )[0],
                    }
                  })
                  .filter((i) => i.name.includes('Cellular'))
                this.summary = summary
              }
            })
          })
      })
    })
  },
  methods: {
    onReset() {
      const hardwareId = this.$route.params.hardwareId
      httpServices.resetHardware(hardwareId).then((_r) => {
        this.$notification.success({
          message: `${hardwareId} device is resetting`,
        })
      })
    },
    onDelete() {
      const hardwareId = this.$route.params.hardwareId
      httpServices
        .deleteMessagesFromDevice(this.hardware.meta.serial)
        .then((_r) => {
          this.$notification.success({
            message: `${hardwareId} device is removing messages`,
          })
        })
    },
  },
}
</script>

<template>
  <Layout>
    <div class="ant-row profile--container">
      <div class="breadcrumb--container">
        <a-breadcrumb>
          <a-breadcrumb-item href="#">
            <router-link to="/">
              <a-icon type="home" />
            </router-link>
          </a-breadcrumb-item>
          <a-breadcrumb-item href="#">
            <router-link to="/sim-management">
              <a-icon type="phone" />
              <span>SIM Management</span>
            </router-link>
          </a-breadcrumb-item>
          <a-breadcrumb-item>
            <a-icon type="profile" />
            Hardware Summary
          </a-breadcrumb-item>
        </a-breadcrumb>
      </div>
      <div class="form-container">
        <div
          class="header"
          style="
            padding: 1.2rem;
            margin-bottom: 0;
            text-align: center;
            background: #141414;
            border-radius: 4px 4px 0 0;
          "
        >
          <h4 style="margin: 0; font-weight: 100; color: #fff">
            <a-icon type="control" style="padding-right: 4px" />
            <b>{{ $route.params.hardwareId.toUpperCase() }}</b> &middot;
            {{ hardware ? hardware.meta.serial : '' }}
          </h4>
        </div>

        <template v-if="hardware && hardware.meta && summary">
          <a-descriptions
            title="Hardware Summary"
            bordered
            style="margin-top: 1rem"
          >
            <a-descriptions-item v-if="hardware.meta.status" label="Status">
              <a-badge
                v-if="hardware.meta.status.toLowerCase() === 'online'"
                status="success"
                text="Online"
              />
              <a-badge
                v-else-if="hardware.meta.status.toLowerCase() === 'starting'"
                status="processing"
                text="Starting"
              />
              <a-badge v-else text="Offline" />
            </a-descriptions-item>
            <a-descriptions-item label="Location">
              {{ hardware.meta.location }}
            </a-descriptions-item>
            <a-descriptions-item label="Timezone">
              {{ hardware.meta.timezone }}
            </a-descriptions-item>
            <a-descriptions-item label="Last Online">
              {{ summary.last_online }}
            </a-descriptions-item>
            <a-descriptions-item label="Last Offline">
              {{ summary.offline_at }}
            </a-descriptions-item>
            <a-descriptions-item label="Uptime">
              {{ summary.uptime }}
            </a-descriptions-item>
            <a-descriptions-item label="Delete Hardware Messages">
              <a-popconfirm
                title="Sure to reset this hardware?"
                @confirm="() => onDelete()"
              >
                <a href="javascript:;" title="Reset Hardware">
                  <a-button type="danger" icon="delete" size="small"
                    >Delete
                  </a-button>
                </a>
              </a-popconfirm>
            </a-descriptions-item>
            <a-descriptions-item label="Reset Hardware">
              <a-popconfirm
                title="Sure to reset this hardware?"
                @confirm="() => onReset()"
              >
                <a href="javascript:;" title="Reset Hardware">
                  <a-button type="dashed" icon="poweroff" size="small"
                    >Reset
                  </a-button>
                </a>
              </a-popconfirm>
            </a-descriptions-item>
          </a-descriptions>
          <a-card title="Interfaces" style="margin-top: 1rem">
            <HardwareSummaryTable
              v-if="hardware && hardware.meta && summary"
              :interfaces="summary.interfaces"
            >
            </HardwareSummaryTable>
          </a-card>
        </template>
        <template v-else>
          <div class="loader--container">
            <a-icon
              type="loading"
              style="padding-left: 4px; font-size: 24px; color: #1890ff"
              spin
            />
          </div>
        </template>
      </div>
    </div>
  </Layout>
</template>
